import { newHtml } from 't-lib-ui/helpers';
import type { Post as FeedPost } from '../schema-copy/feed-schema';
import { formatPublishedTime, formatListTime, stripHtml, parseAuthor } from '../utils/formatting';
import { createLockSvg, createPlusSvg, createClockSvg, createShareSvg, createSpinnerSvg, createPlaySvg } from './svg-helpers';
import type { YoutubeVideo } from '../schema-copy/feed-schema';
import { safeSetHTML, retryNoReferrer, stabilizeImageDimensions, hydrateDigestPostEmbeds, hydrateImageOnlyPost, hideGhostEmbedFallbackImages, normalizeAsides, collapseBrRuns, hydrateAttributedQuotes, hydrateUnsupportedCharts, hydrateInstagramEmbeds, hydrateTikTokEmbeds, hydrateThreadsEmbeds, showImageViewer, isPostImageInBody } from './post-content';
import { gateEmbeds, persistentConsent, isEmbedAllowed, createEmbedGate } from './embed-consent';
import { postSiteClass } from './site-css';

export interface PostEntry {
  feedId: string;
  postIndex: number;
  element: HTMLDivElement | null;
  height: number;
  loaded: boolean;
  data: FeedPost | null;
  isRead: boolean;
  // Read state when the view was built. "Keep Unread" only reverts posts read
  // during this session (auto-marks), never posts that were already read before.
  initiallyRead: boolean;
  isStarred: boolean;
  isReadLater: boolean;
  manuallyUnread: boolean;
  keepUnread: boolean;
}

export function createLoadingPlaceholder(height = 100): HTMLDivElement {
  return newHtml('div', { class: 'postItem', 'data-unrendered': 'true' },
    newHtml('div', { style: { display: 'flex', justifyContent: 'center', alignItems: 'center', height: `${height}px` } },
      height > 40 ? createSpinnerSvg() : '')) as HTMLDivElement;
}

// Skeleton row for a not-yet-loaded list entry. Mirrors createListRow's column
// layout so rows don't shift when real content replaces them, and shows a calm
// shimmer instead of an empty gap or a per-row spinner (which would look busy at
// list density). The title bar width is varied per row so a run of placeholders
// doesn't read as a single solid block.
export function createListSkeleton(isSingleFeed = false, index = 0): HTMLDivElement {
  const titleWidths = ['62%', '78%', '45%', '70%', '54%'];
  const children: HTMLElement[] = [newHtml('span', { class: 'skelDot' })];
  if (!isSingleFeed)
    children.push(newHtml('span', { class: 'listRowFeed' }, newHtml('span', { class: 'skelBar' })));
  children.push(newHtml('span', { class: 'listRowContent' },
    newHtml('span', { class: 'skelBar', style: { width: titleWidths[index % titleWidths.length] } })));
  children.push(newHtml('span', { class: 'listRowTime' }, newHtml('span', { class: 'skelBar' })));
  return newHtml('div', { class: 'postItem listMode listSkeleton', 'data-unrendered': 'true' }, children) as HTMLDivElement;
}

/** Header action-button slot: plus button when unsubscribed, invisible spacer when subscribed. */
export function createFollowActionSlot(isSubscribed: boolean): HTMLElement {
  if (isSubscribed)
    return newHtml('div', { class: 'followActionSlot', style: { width: '36px', height: '36px', flexShrink: '0' } });
  return newHtml('button', {
    class: 'actionButton followActionSlot flowFocussable sidewayFocus',
    title: 'Follow', 'data-action': 'follow',
  }, createPlusSvg(16)) as HTMLElement;
}

/** Footer separator slot: '+ Follow' button when unsubscribed, null when subscribed (caller skips it). */
export function createFollowSepSlot(isSubscribed: boolean): HTMLElement | null {
  if (isSubscribed)
    return null;
  return newHtml('button', {
    class: 'separatorButton followSepSlot flowFocussable sidewayFocus',
    title: 'Follow', 'data-action': 'follow',
  }, newHtml('span', { class: 'separatorLabel' }, '+ Follow')) as HTMLElement;
}

export function createListRow(
  entry: PostEntry,
  feedName: string,
  isSingleFeed?: boolean,
  isSavedList?: boolean,
): HTMLDivElement {
  const post = entry.data!;
  const children: HTMLElement[] = [];
  const displayRead = isSavedList ? false : entry.isRead;

  // Read indicator: lock if manually kept unread, dot otherwise (no lock in saved lists)
  if (!isSavedList && (entry.keepUnread || entry.manuallyUnread))
    children.push(newHtml('span', { class: 'listRowLock' }, createLockSvg(12)));
  else
    children.push(newHtml('span', { class: `readDot ${displayRead ? 'read' : 'unread'}` }));

  // Column 1: Feed name (omitted for single feed view)
  if (!isSingleFeed)
    children.push(newHtml('span', { class: 'listRowFeed' }, feedName));

  // Column 2: Title + body preview (Tweet-style posts have no title — just the preview)
  const bodyPreview = stripHtml(post.description, post.title ? Math.max(50, 300 - post.title.length) : 300);
  const contentChildren: HTMLElement[] = [];
  if (post.title)
    contentChildren.push(newHtml('span', { class: 'listRowTitle' }, post.title));
  if (bodyPreview)
    contentChildren.push(newHtml('span', { class: `listRowPreview${post.title ? '' : ' noTitle'}` }, post.title ? ` — ${bodyPreview}` : bodyPreview));
  children.push(newHtml('span', { class: 'listRowContent' }, contentChildren));

  // Column 3: Time/date
  children.push(newHtml('span', { class: 'listRowTime' }, post.pubDate ? formatListTime(post.pubDate) : ''));

  return newHtml('div', {
    class: `postItem listMode flowFocussable${displayRead ? ' read' : ''}`,
    tabindex: '0',
    'data-unrendered': 'true',
    'data-feed-id': entry.feedId,
    'data-post-number': String(entry.postIndex),
  }, children) as HTMLDivElement;
}

// YouTube video ids are a fixed, URL-safe alphabet; validate before interpolating
// into an embed URL so a malformed id can never break out of the src attribute.
const YOUTUBE_ID_RE = /^[A-Za-z0-9_-]{6,20}$/;

/**
 * Build the click-to-play YouTube embed: a thumbnail with a play overlay that, on
 * click, swaps itself for the privacy-enhanced (youtube-nocookie) iframe. Optionally
 * followed by a small stats line (views, likes).
 */
function createYoutubeBlock(videoId: string, thumbnailUrl: string | undefined, title: string, yt: YoutubeVideo): HTMLElement[] {
  const player = newHtml('div', { class: 'youtubePlayer' }) as HTMLDivElement;
  const thumb = newHtml('img', {
    class: 'youtubeThumb',
    src: thumbnailUrl || `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`,
    alt: title, loading: 'lazy',
  });
  retryNoReferrer(thumb as HTMLImageElement);
  player.append(thumb, newHtml('span', { class: 'youtubePlayButton', 'aria-hidden': 'true' }, createPlaySvg(32)));
  player.addEventListener('click', () => {
    const iframe = newHtml('iframe', {
      class: 'youtubeIframe',
      src: `https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&rel=0&enablejsapi=1`,
      title,
      frameborder: '0',
      // The document meta referrer is same-origin; YouTube errors (153) without referrer
      // info. 'origin' sends only the origin, not what the reader is reading.
      referrerpolicy: 'origin',
      allow: 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share',
      allowfullscreen: 'true',
    }) as HTMLIFrameElement;
    // Belt-and-braces: autoplay=1 alone is unreliable in cross-origin iframes
    // because user-activation doesn't always propagate. Sending the playVideo
    // command via postMessage right after load forces playback inside the
    // ~5s user-activation window from the click that triggered this handler.
    iframe.addEventListener('load', () => {
      iframe.contentWindow?.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
    });
    player.innerHTML = '';
    player.classList.add('playing');
    player.appendChild(iframe);
  });

  // Unconsented: the privacy overlay sits on top of the click-to-play layer. Allowing
  // removes only the overlay (via activateEmbeds) — the thumbnail stays, so a feed full of
  // videos still mounts zero YouTube players until each is clicked.
  const out: HTMLElement[] = [isEmbedAllowed('youtube') ? player : createEmbedGate(player, 'youtube', persistentConsent)];
  const stats: string[] = [];
  if (yt.views != null) stats.push(`${yt.views.toLocaleString('en-US')} view${yt.views == 1 ? '' : 's'}`);
  if (yt.ratingCount != null) stats.push(`${yt.ratingCount.toLocaleString('en-US')} like${yt.ratingCount == 1 ? '' : 's'}`);
  if (stats.length)
    out.push(newHtml('div', { class: 'youtubeStats' }, stats.join('  ·  ')));
  return out;
}

export function createPostElement(
  entry: PostEntry,
  feedName: string,
  siteUrl: string | undefined,
  viewMode: 'full' | 'single' | 'list',
  isAutoMarkRead?: boolean,
  isSingleFeed?: boolean,
  isSavedList?: boolean,
  isSubscribed?: boolean,
): HTMLDivElement {
  if (viewMode == 'list')
    return createListRow(entry, feedName, isSingleFeed, isSavedList);
  // 'full' and 'single' render identical post cards; they only differ in
  // whether the HeadlineList column is shown next to the PostView.
  const post = entry.data!;
  const link = post.link || '#';
  const EXT_LINK = { target: '_blank', rel: 'noopener noreferrer' } as const;
  const displayRead = isSavedList ? false : entry.isRead;

  // .postMeta — newsletter feeds store mailto: in siteUrl; only link real web URLs
  const linkableSiteUrl = siteUrl && /^https?:\/\//i.test(siteUrl) ? siteUrl : undefined;
  const feedEl = linkableSiteUrl
    ? newHtml('a', { class: 'postFeed flowFocussable', href: linkableSiteUrl, ...EXT_LINK }, feedName)
    : newHtml('span', { class: 'postFeed' }, feedName);
  const meta = newHtml('div', { class: 'postMeta' }, [
    feedEl,
    newHtml('span', { class: 'postDivider' }, '•'),
    newHtml('span', { class: 'postTime' }, post.pubDate ? formatPublishedTime(post.pubDate) : ''),
  ]);

  // .postActions
  const actionChildren: HTMLElement[] = [];
  if (isSavedList) {
    actionChildren.push(
      createFollowActionSlot(isSubscribed ?? false),
      newHtml('button', {
        class: `actionButton flowFocussable sidewayFocus${entry.isReadLater ? ' readLater' : ''}`,
        title: 'Read later', 'data-action': 'toggleReadLater',
      }, createClockSvg(18)),
      newHtml('button', {
        class: `actionButton flowFocussable sidewayFocus${entry.isStarred ? ' starred' : ''}`,
        title: 'Star', 'data-action': 'toggleStar',
      }, '★'),
    );
  } else {
    actionChildren.push(
      newHtml('button', {
        class: `actionButton flowFocussable sidewayFocus${isAutoMarkRead && entry.keepUnread ? ' keepActive' : ''}`,
        title: isAutoMarkRead ? 'Keep unread' : (entry.isRead ? 'Mark unread' : 'Mark read'),
        'data-action': 'toggleRead',
      }, [newHtml('span', { class: `readDot ${entry.isRead ? 'read' : 'unread'}` }), createLockSvg(14)]),
      newHtml('button', {
        class: `actionButton flowFocussable sidewayFocus${entry.isReadLater ? ' readLater' : ''}`,
        title: 'Read later', 'data-action': 'toggleReadLater',
      }, createClockSvg(18)),
      newHtml('button', {
        class: `actionButton flowFocussable sidewayFocus${entry.isStarred ? ' starred' : ''}`,
        title: 'Star', 'data-action': 'toggleStar',
      }, '★'),
    );
  }

  const header = newHtml('div', { class: 'postHeader' }, [
    meta,
    newHtml('div', { class: 'postActions' }, actionChildren),
  ]);

  // h1.postTitle — Tweet-style posts have no title, so it's simply omitted.
  const articleChildren: (HTMLElement | SVGElement)[] = [header];
  if (post.title) {
    const h1 = newHtml('h1', { class: 'postTitle' },
      newHtml('a', { href: link, ...EXT_LINK, class: 'flowFocussable' }, post.title));
    articleChildren.push(h1);
  }

  // .postByline (optional). Show the author's real name by default; when an email is also known,
  // clicking toggles to "Name <email>" and back.
  if (post.author) {
    const { name, email } = parseAuthor(post.author);
    const authorSpan = newHtml('span', { class: 'postAuthor' }, name ?? email ?? post.author);
    if (name && email) {
      authorSpan.classList.add('postAuthorToggle');
      authorSpan.setAttribute('title', 'Click to show the email address');
      let showFull = false;
      authorSpan.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        showFull = !showFull;
        authorSpan.textContent = showFull ? `${name} <${email}>` : name;
      });
    }
    articleChildren.push(newHtml('div', { class: 'postByline' }, ['By ', authorSpan]));
  }

  // .youtubePlayer — for YouTube feed posts, an embedded click-to-play video
  // replaces the static image; the description still renders below in .postBody.
  const ytVideoId = post.youtube?.videoId && YOUTUBE_ID_RE.test(post.youtube.videoId) ? post.youtube.videoId : undefined;
  if (ytVideoId)
    articleChildren.push(...createYoutubeBlock(ytVideoId, post.imageUrl, post.title || '', post.youtube!));

  // .postImage (optional, skip if already in body)
  const imageFile = post.imageUrl?.split('/').pop()?.split('?')[0]?.toLowerCase();
  if (!ytVideoId && imageFile && imageFile.length > 8 && !isPostImageInBody(post.imageUrl, post.description)) {
    const img = newHtml('img', { src: post.imageUrl!, alt: post.title || '', style: { cursor: 'pointer' } });
    retryNoReferrer(img as HTMLImageElement);
    stabilizeImageDimensions(img as HTMLImageElement);
    img.addEventListener('click', () => showImageViewer(post.imageUrl!, post.title || ''));
    articleChildren.push(newHtml('div', { class: 'postImage' }, img));
  }

  // .postBody — font family/size come from CSS variables and the postFontSans
  // class on the surrounding PostView/SinglePostView root, so appearance
  // changes restyle already-rendered posts without a rebuild.
  const body = newHtml('div', { class: 'postBody' });
  safeSetHTML(body, post.description || '');
  body.querySelectorAll('a[href]').forEach(a => a.classList.add('flowFocussable'));
  hideGhostEmbedFallbackImages(body);
  normalizeAsides(body);
  collapseBrRuns(body);
  hydrateAttributedQuotes(body);
  hydrateUnsupportedCharts(body);
  hydrateDigestPostEmbeds(body);
  hydrateImageOnlyPost(body);
  hydrateInstagramEmbeds(body);
  hydrateTikTokEmbeds(body);
  hydrateThreadsEmbeds(body);
  // Must run after the hydrators (they establish the blockquote classes gating selects on)
  // and while body is still detached, so a gated iframe never issues a request.
  gateEmbeds(body, persistentConsent);
  articleChildren.push(body);

  // .cardActionsRow
  const sepBtn = (cls: string, attrs: any, children: any) =>
    newHtml('button', { class: `separatorButton flowFocussable sidewayFocus${cls}`, ...attrs }, children);
  const sepLabel = (text: string) => newHtml('span', { class: 'separatorLabel' }, text);

  const secondAction = isSavedList
    ? createFollowSepSlot(isSubscribed ?? false)
    : (() => {
        const readIcon = isAutoMarkRead
          ? (() => { const l = createLockSvg(14); l.setAttribute('class', 'separatorIcon'); return l; })()
          : newHtml('span', { class: `readDot ${entry.isRead ? 'read' : 'unread'}` });
        return sepBtn(`${isAutoMarkRead ? ' keepMode' : ''}${isAutoMarkRead && entry.keepUnread ? ' keepActive' : ''}`,
          { title: isAutoMarkRead ? 'Keep unread' : (entry.isRead ? 'Mark as unread' : 'Mark as read'), 'data-action': 'toggleRead' },
          [readIcon, sepLabel(isAutoMarkRead ? (entry.keepUnread ? 'Kept' : 'Keep') : (entry.isRead ? 'Read' : 'Unread'))]);
      })();

  const sepChildren: (HTMLElement | null)[] = [
    newHtml('a', { href: link, ...EXT_LINK, class: 'separatorButton flowFocussable', title: 'View original post' }, [
      newHtml('span', { class: 'separatorIcon' }, '↗'), sepLabel('Original Post'),
    ]),
    secondAction,
    sepBtn(entry.isReadLater ? ' readLater' : '',
      { title: 'Read later', 'data-action': 'toggleReadLater' },
      [createClockSvg(16), sepLabel('Read later')]),
    sepBtn(entry.isStarred ? ' starred' : '',
      { title: entry.isStarred ? 'Unstar' : 'Star', 'data-action': 'toggleStar' },
      [newHtml('span', { class: 'separatorIcon' }, '★'), sepLabel(entry.isStarred ? 'Unstar' : 'Star')]),
    sepBtn('',
      { title: 'Share', 'data-action': 'share' },
      [createShareSvg(14), sepLabel('Share')]),
  ];
  articleChildren.push(newHtml('div', { class: 'cardActionsRow' },
    newHtml('div', { class: 'separatorActions' }, sepChildren.filter(Boolean) as HTMLElement[])));

  // feed-<domain> scopes the site's stylesheet, if it has one (components/site-css.ts).
  const siteClass = postSiteClass(siteUrl);
  const article = newHtml('article', {
    class: siteClass ? `postCard ${siteClass}` : 'postCard',
    'data-post-id': `${entry.feedId}-${entry.postIndex}`,
    'data-feed-id': entry.feedId,
    'data-post-number': String(entry.postIndex),
    'data-is-read': String(displayRead),
  }, articleChildren);

  return newHtml('div', {
    class: 'postItem',
    'data-unrendered': 'true',
  }, article) as HTMLDivElement;
}

export function updatePostStatus(el: HTMLDivElement, entry: PostEntry, isAutoMarkRead?: boolean) {
  // Update list row read indicator (dot vs lock)
  if (el.classList.contains('listMode')) {
    const indicator = el.querySelector(':scope > .readDot, :scope > .listRowLock');
    if (indicator) {
      if (entry.keepUnread || entry.manuallyUnread) {
        if (indicator.classList.contains('readDot')) {
          const lock = newHtml('span', { class: 'listRowLock' }, createLockSvg(12));
          indicator.replaceWith(lock);
        }
      } else {
        if (indicator.classList.contains('listRowLock')) {
          const dot = newHtml('span', { class: `readDot ${entry.isRead ? 'read' : 'unread'}` });
          indicator.replaceWith(dot);
        } else {
          indicator.className = `readDot ${entry.isRead ? 'read' : 'unread'}`;
        }
      }
    }
    el.classList.toggle('read', entry.isRead && !entry.keepUnread && !entry.manuallyUnread);
  }

  const article = el.querySelector('article');
  if (!article) return;

  article.dataset.isRead = String(entry.isRead);

  el.querySelectorAll('.readDot').forEach(dot =>
    dot.className = `readDot ${entry.isRead ? 'read' : 'unread'}`
  );

  el.querySelectorAll<HTMLElement>('[data-action="toggleRead"]').forEach(btn => {
    if (isAutoMarkRead) {
      btn.classList.toggle('keepActive', entry.keepUnread);
      btn.title = 'Keep unread';
      if (btn.classList.contains('separatorButton')) {
        const label = btn.querySelector('.separatorLabel');
        if (label) label.textContent = entry.keepUnread ? 'Kept' : 'Keep';
      }
    } else if (btn.classList.contains('separatorButton')) {
      btn.title = entry.isRead ? 'Mark as unread' : 'Mark as read';
      const label = btn.querySelector('.separatorLabel');
      if (label) label.textContent = entry.isRead ? 'Read' : 'Unread';
    } else {
      btn.title = entry.isRead ? 'Mark unread' : 'Mark read';
    }
  });

  el.querySelectorAll<HTMLElement>('[data-action="toggleStar"]').forEach(btn => {
    btn.classList.toggle('starred', entry.isStarred);
    if (btn.classList.contains('separatorButton')) {
      btn.title = entry.isStarred ? 'Unstar' : 'Star';
      const label = btn.querySelector('.separatorLabel');
      if (label) label.textContent = entry.isStarred ? 'Unstar' : 'Star';
    }
  });

  el.querySelectorAll<HTMLElement>('[data-action="toggleReadLater"]').forEach(btn =>
    btn.classList.toggle('readLater', entry.isReadLater)
  );
}
